import { useContext, useEffect, useMemo, useRef } from 'react';
import {
    ActionDependant,
    evaluateTemplateValue,
    GlobalDataContext,
    propsDataLocationToPathAndValue,
    TemplateContext,
} from '@ea-lab/reactive-json';
import { Check, Lock, Ban, Info } from 'lucide-react';
import { getAllowedTargets, canTransition } from '../kanban/kanbanTransitionPolicy';
import { notifyRefusal, classifyRefusal } from '../kanban/kanbanNotify';

/**
 * TaskStateFresco — « fresque » d'état : un PIPELINE DE CHEVRONS (flux vers la
 * droite) montrant TOUS les états du workflow de la tâche, où l'on clique pour
 * changer d'état.
 *
 * Lecture visuelle :
 *   • flux principal = chevrons emboîtés pointant à droite (poids croissant) ;
 *     franchi = iris clair + ✓, courant = iris plein + ●, cible AUTORISÉE =
 *     chevron iris clair cliquable, cible VERROUILLÉE = chevron gris + cadenas.
 *   • états danger (Annulé/Bloqué) = chevrons ROSE détachés à droite du flux.
 *   • sous le flux, une ligne d'EXPLICATION dit POURQUOI quand une transition
 *     attendue est bloquée (sous-tâches ouvertes / rôle) ou quand la tâche est
 *     coincée — pour qu'on ne reste jamais devant un blocage inexpliqué.
 *
 * La DÉCISION d'autorisation reste déléguée à la source unique de policy
 * (getAllowedTargets / canTransition) : reachable ∩ rôle ∩ sous-tâches, comme le
 * board / le menu de carte. Le contexte est amorcé par TaskEdit (setPolicyContext).
 * Type sans workflow (legacy) → rend `null` (le parent affiche le repli).
 *
 * Props : dataLocation (état), workflow ({workflowStates, workflowTransitions}),
 * task (forme policy { data:{id,type,state} }), taskType (id), saveStatus.
 */

const DANGER_KEYS = ['cancel', 'block', 'annul', 'bloqu', 'refus'];
const isDangerState = (id = '') => DANGER_KEYS.some((k) => String(id).toLowerCase().includes(k));

// Texte d'explication par nature de blocage (canTransition().reason).
const REASON_HINT = {
    subtasks: 'Des sous-tâches encore ouvertes doivent être terminées avant cette transition.',
    permission: 'Votre rôle sur ce projet ne permet pas ces transitions.',
    workflow: 'Le workflow de ce type de tâche ne prévoit pas d’autre transition depuis cet état.',
};

// Profondeur (px) de la pointe du chevron + chevauchement à gauche.
const TIP = 11;

export const TaskStateFresco = ({ props, datafield, path }) => {
    const globalDataContext = useContext(GlobalDataContext);
    const templateContext = useContext(TemplateContext);

    const { formData: currentState, formDataPath } = propsDataLocationToPathAndValue({
        currentPath: path,
        datafield,
        dataLocation: props.dataLocation,
        defaultValue: '',
        globalDataContext,
        templateContext,
    });

    const workflow = evaluateTemplateValue({ valueToEvaluate: props.workflow, globalDataContext, templateContext });
    const taskObj = evaluateTemplateValue({ valueToEvaluate: props.task, globalDataContext, templateContext });
    const taskTypeId = evaluateTemplateValue({ valueToEvaluate: props.taskType, globalDataContext, templateContext });
    const saveStatus = evaluateTemplateValue({ valueToEvaluate: props.saveStatus, globalDataContext, templateContext });

    // Parité board : remonter au lecteur la RAISON d'un refus serveur. DataSync
    // écrit ~~.*.status ; sur 'error' on lève le MÊME toast motivé que le board.
    const lastNotifiedRef = useRef('');
    useEffect(() => {
        if (saveStatus?.type === 'error' && saveStatus?.message) {
            if (saveStatus.message !== lastNotifiedRef.current) {
                lastNotifiedRef.current = saveStatus.message;
                notifyRefusal(classifyRefusal(saveStatus.message));
            }
        } else if (saveStatus?.type && saveStatus.type !== 'error') {
            lastNotifiedRef.current = '';
        }
    }, [saveStatus?.type, saveStatus?.message]);

    const handleSelect = (value) => {
        globalDataContext.updateData(value, formDataPath);
    };

    const states = workflow?.workflowStates ?? workflow?.states ?? null;

    const model = useMemo(() => {
        if (!states) return null;

        const base = (taskObj && typeof taskObj === 'object') ? taskObj : { data: {} };
        const baseData = base.data || base;
        const policyTask = {
            data: {
                ...baseData,
                type: baseData.type ?? taskTypeId,
                state: currentState || baseData.state,
            },
        };

        const allowed = new Set(getAllowedTargets(policyTask).map((t) => t.to));

        const entries = Object.entries(states).map(([id, s]) => ({
            id,
            label: s?.label ?? id,
            weight: Number(s?.weight) || 0,
            danger: isDangerState(id),
        }));
        entries.sort((a, b) => a.weight - b.weight);

        const currentEntry = entries.find((e) => e.id === currentState) || null;
        const currentWeight = currentEntry ? currentEntry.weight : null;
        const currentIsDanger = currentEntry ? currentEntry.danger : false;

        const annotate = (e) => {
            const isCurrent = e.id === currentState;
            const isAllowed = allowed.has(e.id);
            const isPast = !isCurrent && !e.danger && !currentIsDanger
                && currentWeight != null && e.weight < currentWeight;
            const verdict = (!isCurrent && !isAllowed) ? canTransition(policyTask, e.id) : null;
            return {
                ...e,
                isCurrent,
                isAllowed,
                isPast,
                clickable: isAllowed && !isCurrent,
                reason: verdict?.message || '',
                reasonKind: verdict?.reason || null,
            };
        };

        const all = entries.map(annotate);
        const main = all.filter((e) => !e.danger);
        const danger = all.filter((e) => e.danger);

        // Explication sous le flux. On n'alerte que sur les blocages ACTIONNABLES
        // (sous-tâches / rôle), ou si la tâche est complètement coincée — pas pour
        // les états simplement non connectés (reason 'workflow' = forme normale).
        const blocked = all.filter((e) => !e.isCurrent && !e.clickable && !e.isPast);
        const kinds = new Set(blocked.map((e) => e.reasonKind).filter(Boolean));
        const allowedCount = all.filter((e) => e.clickable).length;
        let hint = null;
        if (allowedCount === 0 && currentState) {
            const k = kinds.has('subtasks') ? 'subtasks' : kinds.has('permission') ? 'permission' : 'workflow';
            hint = { tone: 'stuck', text: `Cette tâche ne peut pas changer d’état pour l’instant. ${REASON_HINT[k]}` };
        } else if (kinds.has('subtasks') || kinds.has('permission')) {
            const k = kinds.has('subtasks') ? 'subtasks' : 'permission';
            hint = { tone: 'info', text: REASON_HINT[k] };
        }

        return { main, danger, hint };
    }, [states, taskObj, taskTypeId, currentState]);

    if (!model) return null; // legacy (sans workflow) → repli géré par le parent

    const { main, danger, hint } = model;

    // Fond + texte d'un chevron selon son statut (pas de bordure/ring : clip-path
    // les rognerait ; on distingue par les fonds et l'icône).
    const segFill = (st) => {
        if (st.isCurrent) return st.danger ? 'bg-rose-600 text-white' : 'bg-iris-600 text-white';
        if (st.isPast) return 'bg-iris-100 text-iris-700';
        if (st.clickable) return st.danger
            ? 'bg-rose-50 text-rose-600 hover:bg-rose-100'
            : 'bg-iris-50 text-iris-700 hover:bg-iris-100';
        return st.danger ? 'bg-slate-100 text-rose-700' : 'bg-slate-100 text-slate-600'; // verrouillé (contraste AA : slate-400/rose-300 échouaient WCAG 1.4.3)
    };

    const segIcon = (st) => {
        if (st.isPast) return <Check size={13} strokeWidth={3} aria-hidden="true" />;
        if (st.isCurrent) {
            return st.danger
                ? <Ban size={13} strokeWidth={2.5} aria-hidden="true" />
                : <span className="h-2 w-2 rounded-full bg-white" aria-hidden="true" />;
        }
        if (!st.clickable) return <Lock size={12} strokeWidth={2.25} aria-hidden="true" />;
        if (st.danger) return <Ban size={12} strokeWidth={2.25} aria-hidden="true" />;
        return null; // cible autorisée non franchie
    };

    const nodeTitle = (st) => {
        if (st.isCurrent) return `État actuel : ${st.label}`;
        if (st.clickable) return `Passer à « ${st.label} »`;
        if (st.isPast) return `Étape franchie : ${st.label}`;
        return st.reason ? `${st.label} — ${st.reason}` : st.label;
    };

    // Un chevron : <button> si cliquable, sinon <div> (pour que le tooltip/raison
    // s'affiche au survol — un <button disabled> n'émet pas d'événement hover).
    // clip-path : pointe à droite (sauf le dernier, à bord droit) ; chevauchement
    // gauche (-ml + pl) pour emboîter dans la pointe précédente ; z décroissant.
    const renderChevron = (st, idx, total) => {
        const isLast = idx === total - 1;
        const isBlocked = !st.isCurrent && !st.clickable && !st.isPast;
        const clip = isLast
            ? 'polygon(0 0, 100% 0, 100% 100%, 0 100%)'
            : `polygon(0 0, calc(100% - ${TIP}px) 0, 100% 50%, calc(100% - ${TIP}px) 100%, 0 100%)`;
        const style = {
            zIndex: total - idx,
            clipPath: clip,
            ...(isLast ? {} : { filter: 'drop-shadow(1.5px 0 0 rgb(255 255 255))' }),
        };
        const cls = [
            'relative flex h-9 items-center gap-1.5 whitespace-nowrap text-xs font-semibold transition-colors flex-shrink-0 select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-iris-400',
            idx === 0 ? 'pl-3.5' : '-ml-2.5 pl-5',
            isLast ? 'pr-4' : 'pr-5',
            segFill(st),
            st.clickable ? 'cursor-pointer' : isBlocked ? 'cursor-help' : 'cursor-default',
        ].join(' ');

        const inner = (
            <>
                {st.isCurrent && <span className="sr-only">État actuel : </span>}
                {segIcon(st)}
                <span>{st.label}</span>
            </>
        );

        if (st.clickable) {
            return (
                <button key={st.id} type="button" onClick={() => handleSelect(st.id)} title={nodeTitle(st)} style={style} className={cls}>
                    {inner}
                </button>
            );
        }
        return (
            <div
                key={st.id}
                role="img"
                aria-label={nodeTitle(st)}
                aria-current={st.isCurrent ? 'step' : undefined}
                title={nodeTitle(st)}
                style={style}
                className={cls}
            >
                {inner}
            </div>
        );
    };

    return (
        <ActionDependant {...props}>
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-3 py-3 sm:px-4">
                <div className="mb-2.5 flex items-center gap-2">
                    <span className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Statut</span>
                    <span className="text-[11px] text-slate-300">·</span>
                    <span className="text-[11px] text-slate-500">workflow</span>
                </div>

                <div className="flex items-center overflow-x-auto pb-1 scrollbar-none" role="group" aria-label="État de la tâche">
                    <div className="flex items-center">
                        {main.map((st, idx) => renderChevron(st, idx, main.length))}
                    </div>

                    {danger.length > 0 && (
                        <div className="flex items-center pl-2 sm:pl-3 ml-2 sm:ml-3 border-l border-dashed border-slate-200">
                            {danger.map((st, idx) => renderChevron(st, idx, danger.length))}
                        </div>
                    )}
                </div>

                {hint && (
                    <div
                        className={`mt-2.5 flex items-start gap-2 rounded-lg px-3 py-2 text-xs ${
                            hint.tone === 'stuck'
                                ? 'bg-amber-50 border border-amber-200 text-amber-800'
                                : 'bg-slate-50 border border-slate-200 text-slate-600'
                        }`}
                        role="note"
                    >
                        {hint.tone === 'stuck'
                            ? <Lock size={14} className="mt-px flex-shrink-0" aria-hidden="true" />
                            : <Info size={14} className="mt-px flex-shrink-0 text-slate-400" aria-hidden="true" />}
                        <span>{hint.text}</span>
                    </div>
                )}
            </div>
        </ActionDependant>
    );
};
